import { toast } from '@scholastic/mockingjay';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { meQueryOptions } from 'eds-common/api/v2';
import { switchOrganization } from '@/services/auth/auth-service';

export const MINIMUM_SWITCH_DURATION_MS = 1000;

function isPreservedQueryKey(queryKey: readonly unknown[]): boolean {
  return [['clientEnvs'], meQueryOptions().queryKey].some(
    (preserved) => JSON.stringify(preserved) === JSON.stringify(queryKey)
  );
}

export const SWITCH_ORGANIZATION_ERROR_MESSAGE =
  'We could not switch your organization. Please try again.';

export function useSwitchOrganization() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [switchingToOrgName, setSwitchingToOrgName] = useState<string | null>(
    null
  );

  const switchOrg = useCallback(
    (orgId: string, orgName: string): void => {
      setSwitchingToOrgName(orgName);
      const startedAt = Date.now();

      const run = async () => {
        const meResponse = await switchOrganization(orgId);

        queryClient.setQueryData(meQueryOptions().queryKey, meResponse);

        queryClient.removeQueries({
          predicate: ({ queryKey }) => !isPreservedQueryKey(queryKey),
        });

        navigate('/');

        const elapsed = Date.now() - startedAt;
        if (elapsed < MINIMUM_SWITCH_DURATION_MS) {
          await new Promise((resolve) => {
            setTimeout(resolve, MINIMUM_SWITCH_DURATION_MS - elapsed);
          });
        }

        toast.success(`Switched to ${orgName}`);
      };

      run()
        .catch((error) => {
          console.error(error);
          toast.error(SWITCH_ORGANIZATION_ERROR_MESSAGE);
        })
        .finally(() => setSwitchingToOrgName(null));
    },
    [navigate, queryClient]
  );

  return { switchingToOrgName, switchOrg };
}
